/**
 * @module engine/export/export3dPdf
 * @summary Export the 3D view as a PDF view portfolio — a different document
 *   from the 2D layout sheet set, on purpose.
 * @description
 *   The two PDF buttons produce two deliverables, and conflating them would make
 *   both worse:
 *
 *   | | **2D — Layout sheet set** | **3D — View portfolio** |
 *   |---|---|---|
 *   | What it is | The engineering document | The visualisation |
 *   | Scale | Plotted to a standard ratio, measurable | **NTS** — an isometric has no single scale |
 *   | Contents | Layout + equipment schedule | Framed views, captioned |
 *   | Used for | Setting out, fabrication, quoting | Explaining the plant, approvals, presentations |
 *
 *   So this one deliberately does NOT carry a scale bar, a plot ratio or grid
 *   references. Stating a scale on a perspective view invites a supplier to
 *   measure something they must not measure, and a drawing that invites a wrong
 *   measurement is worse than one that admits it cannot be measured.
 *
 *   What it does carry is the same frame, the same title block and the same
 *   issue status as the layout set, so the two read as sheets of one package,
 *   plus the equipment schedule when asked — a reader looking at a 3D view and
 *   wondering "what is that vessel" needs the tag list as much as anyone.
 *
 *   The views themselves are captured by the CALLER (see `Engine3D.captureViews`)
 *   and passed in. This module never imports the 3D engine: it knows how to lay
 *   images on paper and nothing about how they were rendered.
 */
import { S } from '../state';
import { toast } from '../../lib/toast';
import { dlText } from '../../lib/download';
import { PAPERS, Sheet, sheetGeometry, type PaperName, type TitleInfo } from './pdf/sheet';
import { buildSchedule, drawSchedule, scheduleCapacity, scheduleCsv } from './pdf/schedule';
import { drawViewFull, drawViewGrid, viewSheetCount, type SheetImage } from './pdf/views';

/** How the views are arranged on paper. */
export type ViewLayout = 'full' | 'grid';

export interface Pdf3dOptions {
  paper?: PaperName;
  orientation?: 'landscape' | 'portrait';
  /** Printed in the title block, e.g. `'ISSUED FOR APPROVAL'`. */
  status?: string;
  /** Faint diagonal overprint, for a sheet nobody may build from. */
  watermark?: string;
  /** `'full'` gives each view its own sheet; `'grid'` puts four to a page. */
  layout?: ViewLayout;
  includeSchedule?: boolean;
  scheduleCsvToo?: boolean;
  /** The captured views, in the order they should appear. */
  views?: SheetImage[];
  /**
   * How many views the caller intends to capture, for planning the sheet count
   * before spending a second rendering them. Ignored once `views` is supplied.
   */
  viewCount?: number;
}

const DEFAULTS: Required<
  Omit<Pdf3dOptions, 'watermark' | 'views' | 'viewCount'>
> = {
  paper: 'A3',
  orientation: 'landscape',
  status: 'ISSUED FOR APPROVAL',
  layout: 'grid',
  includeSchedule: false,
  scheduleCsvToo: false,
};

/** What a 3D export will produce, worked out without building it. */
export interface Plan3d {
  paper: PaperName;
  orientation: 'landscape' | 'portrait';
  layout: ViewLayout;
  views: number;
  viewSheets: number;
  scheduleRows: number;
  scheduleSheets: number;
  sheets: number;
}

/**
 * Work out the sheet count without writing a PDF, so the dialog and the title
 * block agree — the same reason `planExport` exists on the 2D side.
 */
export function plan3dExport(options: Pdf3dOptions = {}): Plan3d | null {
  const o = { ...DEFAULTS, ...options };
  const views = options.views?.length ?? Math.max(0, options.viewCount ?? 0);
  if (!views) return null;

  const paper = PAPERS[o.paper] ? o.paper : 'A3';
  const g = sheetGeometry(paper, o.orientation);
  const viewSheets = viewSheetCount(views, o.layout);

  const scaleMm = S.scaleMm > 0 ? S.scaleMm : 25;
  const levels = new Map(S.levels.map((l) => [l.id, l.name]));
  const rowCount = o.includeSchedule
    ? buildSchedule(S.shapes, scaleMm, (id) => (id ? levels.get(id) || '' : '')).length
    : 0;
  const scheduleSheets = rowCount ? Math.ceil(rowCount / scheduleCapacity(g.plot.h, true)) : 0;

  return {
    paper,
    orientation: o.orientation,
    layout: o.layout,
    views,
    viewSheets,
    scheduleRows: rowCount,
    scheduleSheets,
    sheets: viewSheets + scheduleSheets,
  };
}

/** A finished, unsaved 3D portfolio. */
export interface Built3dSheetSet {
  doc: import('jspdf').jsPDF;
  plan: Plan3d;
  rows: ReturnType<typeof buildSchedule>;
  stem: string;
}

/**
 * Build the portfolio and hand it back UNSAVED — the same split as
 * `buildSheetSet`, and for the same reason: downloading needs a browser, and the
 * self-check needs to read the finished content stream back.
 */
export async function build3dSheetSet(
  jsPDFCtor: typeof import('jspdf').jsPDF,
  options: Pdf3dOptions = {},
): Promise<Built3dSheetSet | null> {
  const o = { ...DEFAULTS, ...options };
  const views = options.views ?? [];
  if (!views.length) return null;
  const plan = plan3dExport({ ...options, views });
  if (!plan) return null;

  const paper = plan.paper;
  const name = S.docMeta.docName || 'PFD';
  const doc = new jsPDFCtor({
    orientation: o.orientation,
    unit: 'mm',
    format: paper.toLowerCase(),
    compress: true,
  });
  doc.setProperties({
    title: `${name} — 3D Views`,
    subject: o.status,
    author: S.docMeta.drawnBy || 'Jeddah Cables Company',
    creator: 'JCC PFD Engineering Tool',
  });

  const title = (sheetTitle: string, sheetNo: number): TitleInfo => ({
    docName: name,
    rev: S.docMeta.rev,
    date: S.docMeta.date,
    drawnBy: S.docMeta.drawnBy,
    approvedBy: S.docMeta.approvedBy,
    status: o.status,
    sheetTitle,
    sheetNo,
    sheetCount: plan.sheets,
    // Never a ratio. See the module note: a stated scale on an isometric is an
    // invitation to measure something that cannot be measured.
    scaleText: 'NTS',
    paper,
  });

  let sheetNo = 0;
  const newSheet = (): Sheet => {
    if (sheetNo > 0) doc.addPage(paper.toLowerCase(), o.orientation);
    sheetNo++;
    const sh = new Sheet(doc, paper, o.orientation);
    sh.drawFrame();
    return sh;
  };

  // ═══════════════ The views ═══════════════
  if (o.layout === 'full') {
    for (const v of views) {
      const sh = newSheet();
      drawViewFull(doc, sh.plot, v);
      if (o.watermark) sh.drawWatermark(o.watermark);
      sh.drawTitleBlock(title(v.label.toUpperCase(), sheetNo));
    }
  } else {
    for (let i = 0; i < views.length; i += 4) {
      const page = views.slice(i, i + 4);
      const sh = newSheet();
      drawViewGrid(doc, sh.plot, page);
      if (o.watermark) sh.drawWatermark(o.watermark);
      sh.drawTitleBlock(
        title(plan.viewSheets > 1 ? `3D VIEWS (${sheetNo}/${plan.viewSheets})` : '3D VIEWS', sheetNo),
      );
    }
  }

  // ═══════════════ The schedule ═══════════════
  const scaleMm = S.scaleMm > 0 ? S.scaleMm : 25;
  const levels = new Map(S.levels.map((l) => [l.id, l.name]));
  const rows = o.includeSchedule
    ? buildSchedule(S.shapes, scaleMm, (id) => (id ? levels.get(id) || '' : ''))
    : [];

  let cursor = 0;
  for (let i = 0; i < plan.scheduleSheets; i++) {
    const sh = newSheet();
    const before = cursor;
    cursor = drawSchedule(
      doc,
      sh.plot,
      rows,
      cursor,
      'Sizes are the plan footprint taken from the layout and are approximate — confirm against vendor drawings before fabrication.',
    );
    if (o.watermark) sh.drawWatermark(o.watermark);
    sh.drawTitleBlock(
      title(
        plan.scheduleSheets > 1
          ? `EQUIPMENT SCHEDULE (${i + 1}/${plan.scheduleSheets})`
          : 'EQUIPMENT SCHEDULE',
        sheetNo,
      ),
    );
    // A schedule that stops advancing would loop forever; stop instead.
    if (cursor === before) break;
  }

  const safe = (name || 'PFD').replace(/[^a-zA-Z0-9_\- ]/g, '').trim() || 'PFD';
  return { doc, plan, rows, stem: `JCC_${safe}_Rev${S.docMeta.rev || 'A'}_3D` };
}

/** Build the 3D portfolio and download it. */
export async function export3dPdf(options: Pdf3dOptions = {}): Promise<void> {
  if (!options.views?.length) {
    toast('No 3D views were captured — is there anything in the 3D view?');
    return;
  }

  let jsPDFCtor: typeof import('jspdf').jsPDF;
  try {
    jsPDFCtor = (await import('jspdf')).jsPDF;
  } catch {
    toast('Could not load the PDF writer. Reload the page and try again.');
    return;
  }

  const built = await build3dSheetSet(jsPDFCtor, options);
  if (!built) {
    toast('Nothing to export.');
    return;
  }
  const { doc, plan, rows, stem } = built;
  doc.save(`${stem}.pdf`);
  if (options.scheduleCsvToo && rows.length) {
    dlText(scheduleCsv(rows), `${stem}_equipment.csv`, 'text/csv;charset=utf-8');
  }
  toast(
    `${plan.sheets} sheet${plan.sheets > 1 ? 's' : ''} exported — ` +
      `${plan.views} 3D view${plan.views > 1 ? 's' : ''} on ${plan.paper}.`,
    3500,
  );
}
